import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Dropdown from "../ui/Dropdown";

const RemoveFromLeagueModal = ({
  isOpen,
  onClose,
  user,
  onRemoveFromLeague,
}) => {
  const [selectedLeague, setSelectedLeague] = useState("");
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchUserLeagues = useCallback(async () => {
    if (!user || !user.id) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/users/${user.id}/leagues`);
      const leagueOptions = response.data.leagues.map((league) => ({
        value: league.id,
        label: league.name,
      }));
      setLeagues(leagueOptions);
    } catch (err) {
      setError("Failed to fetch user leagues");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchUserLeagues();
  }, [fetchUserLeagues]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLeague || !user || !user.id) {
      setError("Please select a league");
      return;
    }

    setLoading(true);
    try {
      await onRemoveFromLeague(user.id, selectedLeague);
      setSelectedLeague("");
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.error?.message ||
          "Failed to remove user from league",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedLeague("");
    setError("");
    onClose();
  };

  if (!user || !user.id) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Remove from League">
        <div className="p-6 text-center text-gray-500">Loading user...</div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Remove from League">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Remove <strong>{user?.name}</strong> from a specific league
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div>
          <Dropdown
            label="Select League"
            value={selectedLeague}
            onChange={setSelectedLeague}
            options={leagues}
            placeholder="Choose a league..."
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={loading || !selectedLeague}
          >
            {loading ? "Removing..." : "Remove from League"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RemoveFromLeagueModal;
